/* Copyright 2016-2022
   Kabandy
   Author: Cedric Hoarau
 */

import { UrlParam } from "../System";

import * as ApiRouteFormatter from "./RouteFormatter";
import * as ApiRequestHandler from "./RequestHandler";
import * as ApiFetchHelper from "./FetchHelper";

import "./assets/purify.min.js";

// api-config.json <=> IApiConfigJson
export interface IApiConfigJson {
    apiUrl: string;
    accountsUrl: string;
    viewerUrl: string;
    nlpUrl: string;
}

// is initialized
let _isInitialized: boolean = false;

// api url
let _apiUrl: string = "";

// accounts url
let _accountsUrl: string = "";

// viewer url
let _viewerUrl: string = "";

// NLP model query url
let _nlpUrl: string = "";

// cloud | local
let _cloudRunEnabled: boolean = true;

// api version
enum EApiVersion {
    V1 = "v1"
}
let _apiVersion: EApiVersion = EApiVersion.V1;

// api key
let _apiKey: string = "";

// access token
let _accessToken: string = "";

// OAuth2.0 authorization code
let _authCode: string = "";

// OAuth2.0 authorization state
let _authState : string = "";


export async function initializeKabandyWebApi( apiConfigJson?: IApiConfigJson ): Promise<void> {
    if (!_isInitialized) {
        _apiUrl = apiConfigJson ? apiConfigJson.apiUrl : process.env.KAB_API_URL || "";
        _accountsUrl = apiConfigJson ? apiConfigJson.accountsUrl : process.env.KAB_ACCOUNTS_URL || "";
        _viewerUrl = apiConfigJson ? apiConfigJson.viewerUrl : process.env.KAB_VIEWER_URL || "";
        _nlpUrl = apiConfigJson ? apiConfigJson.nlpUrl : process.env.KAB_NLP_URL || "";

        _cloudRunEnabled = UrlParam.Has(window.location.search, "noCloudRun") ? false : true;

        const apiVersionFromUrl: string | null = UrlParam.Get(window.location.search, "apiVersion");
        if (apiVersionFromUrl) {
            // @ts-ignore
            const sanitizedApiVersionFromUrl: string = DOMPurify.sanitize(apiVersionFromUrl, { USE_PROFILES: { html: false } })
            _apiVersion = <EApiVersion>sanitizedApiVersionFromUrl;
        }

        const apiKeyFromUrl: string | null = UrlParam.Get(window.location.search, "apiKey");
        if (apiKeyFromUrl) {
            // @ts-ignore
            const sanitizedApiKeyFromUrl: string = DOMPurify.sanitize(apiKeyFromUrl, { USE_PROFILES: { html: false } })
            _apiKey = sanitizedApiKeyFromUrl;
        }

        const accessTokenFromUrl: string | null = UrlParam.Get(window.location.search, "accessToken");
        if (accessTokenFromUrl) {
            // @ts-ignore
            const sanitizedAccessTokenFromUrl: string = DOMPurify.sanitize(accessTokenFromUrl, { USE_PROFILES: { html: false } })
            _accessToken = sanitizedAccessTokenFromUrl;
        }

        const authCode : string | null = UrlParam.Get(window.location.search, "code" );
        if( authCode ) {
            // @ts-ignore
            const sanitizedAuthCodeFromUrl: string = DOMPurify.sanitize(authCode, { USE_PROFILES: { html: false } })
            _authCode = sanitizedAuthCodeFromUrl;
        }

        const authState : string | null = UrlParam.Get(window.location.search, "state" );
        if( authState ) {
            // @ts-ignore
            const sanitizedAuthStateFromUrl: string = DOMPurify.sanitize(authState, { USE_PROFILES: { html: false } })
            _authState = sanitizedAuthStateFromUrl;
        }

        _isInitialized = true;
    }
}

export function isCloudRunEnabled(): boolean {
    return _cloudRunEnabled;
}
export function setCloudRunEnabled(cloudRunEnabled: boolean): void {
    _cloudRunEnabled = cloudRunEnabled;
}

export function getApiVersion(): EApiVersion {
    return _apiVersion;
}
export function setApiVersion(apiVersion: string): void {
    _apiVersion = <EApiVersion>apiVersion;
}

export function getApiKey(): string {
    return _apiKey;
}

export function getAccessToken(): string {
    return _accessToken;
}

export function getAuthCode() : string {
    return _authCode;
}

export function getAuthState() : string {
    return _authState;
}

export function getApiGatewayUrl() {
    return _apiUrl;
}

export function getAccountsUrl() {
    return _accountsUrl;
}

export function getViewerIframeUrl() {
    return _viewerUrl;
}

export function getNlpUrl() {
    return _nlpUrl;
}

export { ApiRouteFormatter, ApiRequestHandler, ApiFetchHelper };
